import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, BarChart3, Activity, Package, Table, TrendingUp, Receipt, RotateCcw, PieChart, ArrowLeft } from 'lucide-react';
interface AddWidgetButtonProps {
  onAdd: (type: string, config: any, index?: number) => void;
  availableWidgets?: any[];
  index?: number;
}
export const AddWidgetButton: React.FC<AddWidgetButtonProps> = ({
  onAdd,
  availableWidgets,
  index
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'widget' | 'size'>('widget');
  const [selectedWidget, setSelectedWidget] = useState<{
    type: string;
    config: any;
    title: string;
  } | null>(null);
  const sizeOptions = [{
    size: 'small',
    title: 'Petit',
    description: '1/3 de largeur'
  }, {
    size: 'medium',
    title: 'Moyen',
    description: '1/2 de largeur'
  }, {
    size: 'full',
    title: 'Pleine largeur',
    description: 'Largeur complète'
  }];
  const widgetTypes = [{
    type: 'chart',
    title: 'Graphiques',
    description: 'Ajouter un graphique personnalisé',
    icon: BarChart3,
    configs: [{
      chartType: 'sales',
      title: 'Graphique des ventes détaillé'
    }, {
      chartType: 'inventory',
      title: 'État de l\'inventaire'
    }, {
      chartType: 'stock-levels',
      title: 'Niveaux de stock'
    }, {
      chartType: 'payment-methods',
      title: 'Top produits vendus'
    }, {
      chartType: 'withdrawals-evolution',
      title: 'Évolution des retraits'
    }, {
      chartType: 'withdrawals-pie',
      title: 'Répartition des retraits'
    }]
  }, {
    type: 'kpi',
    title: 'Indicateurs KPI',
    description: 'Ajouter un indicateur de performance',
    icon: Activity,
    configs: [{
      kpiType: 'total-products',
      title: 'Total produits'
    }, {
      kpiType: 'stock-value',
      title: 'Valeur du stock'
    }, {
      kpiType: 'low-stock',
      title: 'Stocks faibles'
    }, {
      kpiType: 'sales-count',
      title: 'Nombre de ventes'
    }, {
      kpiType: 'monthly-revenue',
      title: 'Revenus mensuels'
    }, {
      kpiType: 'daily-revenue',
      title: 'Revenus du jour'
    }, {
      kpiType: 'average-order',
      title: 'Panier moyen'
    }]
  }, {
    type: 'table',
    title: 'Tableaux',
    description: 'Ajouter un tableau de données',
    icon: Table,
    configs: [{
      tableType: 'products',
      title: 'Gestion des produits'
    }, {
      tableType: 'sales-list',
      title: 'Liste des ventes'
    }, {
      tableType: 'best-sellers',
      title: 'Meilleurs vendeurs'
    }, {
      tableType: 'top-withdrawn',
      title: 'Produits les + retirés'
    }]
  }];
  const handleWidgetSelect = (type: string, config: any, title: string) => {
    setSelectedWidget({
      type,
      config,
      title
    });
    setStep('size');
  };
  const handleSizeSelect = (size: string) => {
    if (selectedWidget) {
      onAdd(selectedWidget.type, {
        ...selectedWidget.config,
        size
      }, index);
      setIsOpen(false);
      setStep('widget');
      setSelectedWidget(null);
    }
  };
  const handleBack = () => {
    setStep('widget');
    setSelectedWidget(null);
  };
  const resetDialog = () => {
    setStep('widget');
    setSelectedWidget(null);
  };
  return <Dialog open={isOpen} onOpenChange={open => {
    setIsOpen(open);
    if (!open) resetDialog();
  }}>
      <DialogTrigger asChild>
        
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {step === 'size' && <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>}
            <div>
              <DialogTitle>
                {step === 'widget' ? 'Ajouter un nouveau widget' : 'Choisir la taille'}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {step === 'widget' ? 'Choisissez le type de widget que vous souhaitez ajouter à votre tableau de bord.' : `Sélectionnez la taille pour "${selectedWidget?.title}"`}
              </p>
            </div>
          </div>
        </DialogHeader>

        {step === 'widget' ? availableWidgets && availableWidgets.length ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableWidgets.map(item => <Card key={item.id || item.name} className="cursor-pointer hover:shadow-md transition-all duration-200 border-2 hover:border-primary/50 hover:scale-[1.02]" onClick={() => handleWidgetSelect(item.type, item.config, item.name || item.title)}>
                  <CardContent className="p-4 text-center">
                    <p className="font-medium text-sm">{item.name || item.title}</p>
                  </CardContent>
                </Card>)}
            </div> : <div className="space-y-6">
              {widgetTypes.map(widgetType => <div key={widgetType.type}>
                  <div className="flex items-center gap-2 mb-3">
                    <widgetType.icon className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">{widgetType.title}</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {widgetType.configs.map(config => <Card key={config.title} className="cursor-pointer hover:shadow-md transition-all duration-200 border-2 hover:border-primary/50 hover:scale-[1.02]" onClick={() => handleWidgetSelect(widgetType.type, config, config.title)}>
                        <CardContent className="p-4 text-center">
                          <p className="font-medium text-sm">{config.title}</p>
                        </CardContent>
                      </Card>)}
                  </div>
                </div>)}
            </div> : <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              {sizeOptions.map(sizeOption => <Card key={sizeOption.size} className="cursor-pointer hover:shadow-md transition-all duration-200 border-2 hover:border-primary/50 hover:scale-[1.02]" onClick={() => handleSizeSelect(sizeOption.size)}>
                  <CardContent className="p-4">
                    <div className="text-center">
                      <p className="font-medium">{sizeOption.title}</p>
                      <p className="text-sm text-muted-foreground">{sizeOption.description}</p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>}
      </DialogContent>
    </Dialog>;
};